/**
 * FerrisWheelEffect positions slides on a circular arc like a ferris wheel.
 *
 * Similar to the fan effect but slides stay upright (no rotation),
 * just like cars on a ferris wheel that remain level as the wheel turns.
 *
 * Key difference from fan:
 * - Fan: translate3d(x, y, 0) rotateZ(angle) — slides rotate to follow arc tangent
 * - Ferris Wheel: translate3d(x, y, 0) — same arc Y-offset, but NO rotation
 *
 * Options (namespaced under `ferrisWheel`):
 * - ferrisWheel.radius: Circle radius in pixels (default: 3000, larger = flatter curve)
 * - ferrisWheel.maxAngle: Maximum angle in degrees (default: 90)
 */

// Factory function - class is created when TarotEffect is available
function createFerrisWheelEffect(TarotEffect) {
	return class FerrisWheelEffect extends TarotEffect {
		static effectName = 'ferris-wheel';

		static defaultOptions = { ferrisWheel: { radius: 3000, maxAngle: 90 } };

		/**
		 * Effect configuration rules that define carousel behavior constraints
		 */
		static rules = {
			minSlidesPerView: 1,
			maxSlidesPerView: Infinity,
			loopBuffer: { left: 1, right: 1 }, // Extra slides for arc visibility
		};

		/**
		 * Creates a new ferris wheel effect instance
		 * @param {Object} ctx - The shared context object
		 */
		constructor(ctx) {
			super(ctx);
			const _ = this;

			// Cache last transform to avoid unnecessary style updates
			_.lastTransform = new WeakMap();

			_.#loadOptions();
			_.init();
		}

		#loadOptions() {
			const opts = this.ctx.store.getOptions().ferrisWheel || {};
			this.radius = opts.radius;
			this.maxAngle = opts.maxAngle;
		}

		init() {
			super.init();
		}

		reInit() {
			super.reInit();
			this.#loadOptions();
		}

		/**
		 * Main render function called every animation frame to position slides on the arc
		 * Uses four-pass approach to close gaps caused by arc foreshortening
		 *
		 * @param {Object} frame - Complete frame object with all carousel data
		 * @param {Object} utils - Frame utilities for position calculations
		 */
		render(frame, utils) {
			const _ = this;
			const { slides, widths, animation } = frame;

			_.renderSlideWidth(widths.slide);
			_.renderTrackPosition(animation); // Move the track (keeps slides synchronized during loop shifts)

			const viewportCenter = widths.viewport / 2;
			const radius = _.radius;
			const maxAngle = _.maxAngle;
			const maxAngleRad = maxAngle * (Math.PI / 180);
			const trackPos = animation.trackPosition;

			// ============================================
			// PASS 1: Calculate transforms and arc-based width trims
			// ============================================
			for (let i = 0; i < slides.length; i++) {
				const slide = slides[i];

				// Reset trim values
				slide._leftWidthTrimmed = 0;
				slide._rightWidthTrimmed = 0;

				// Calculate slide's visual center in viewport coordinates
				const slideViewportCenter = slide._centerPoint + trackPos;
				const distanceFromCenter = slideViewportCenter - viewportCenter;

				// Calculate raw normalized distance for visibility check
				const rawNormalizedDist = distanceFromCenter / radius;
				const absRawNormDist = Math.abs(rawNormalizedDist);

				// Mark slides beyond the visible arc
				slide._ferrisHidden = absRawNormDist > 1;

				// Clamp to valid arcsin range [-1, 1] for angle calculation
				const normalizedDist = Math.max(-1, Math.min(1, rawNormalizedDist));

				// Calculate angle (clamped by maxAngle)
				let angle = Math.asin(normalizedDist);
				angle = Math.max(-maxAngleRad, Math.min(maxAngleRad, angle));

				// Store computed values
				slide._ferrisAngle = angle;
				slide._ferrisYOffset = radius * (1 - Math.cos(angle));

				// Width lost due to arc foreshortening: width * (1 - cos(angle))
				// Left slides set leftTrim, right slides set rightTrim
				const widthLost = widths.slide * (1 - Math.cos(angle));
				if (distanceFromCenter < 0) {
					// Slide is LEFT of center - its right edge faces center
					slide._leftWidthTrimmed = widthLost;
				} else if (distanceFromCenter > 0) {
					// Slide is RIGHT of center - its left edge faces center
					slide._rightWidthTrimmed = widthLost;
				}
				// Center slide (distanceFromCenter ≈ 0) gets neither trim

				// Start with track-relative position (no + trackPos - track handles that)
				slide._renderPosition = slide._trackPosition;
			}

			// ============================================
			// PASS 2: Accumulate RIGHT trims (forward, left to right)
			// ============================================
			let totalRemovedRight = 0;
			for (let i = 0; i < slides.length; i++) {
				const slide = slides[i];
				totalRemovedRight += slide._rightWidthTrimmed;
				slide._renderPosition -= totalRemovedRight;
			}

			// ============================================
			// PASS 3: Accumulate LEFT trims (backward, right to left)
			// ============================================
			let totalRemovedLeft = 0;
			for (let i = slides.length - 1; i >= 0; i--) {
				const slide = slides[i];
				totalRemovedLeft += slide._leftWidthTrimmed;
				slide._renderPosition += totalRemovedLeft;
			}

			// ============================================
			// PASS 4: Apply transforms to all slides (NO rotation - slides stay upright)
			// ============================================
			for (let i = 0; i < slides.length; i++) {
				const slide = slides[i];

				// Hide slides beyond the visible arc
				if (slide._ferrisHidden) {
					slide.hide();
					continue;
				}

				// Always show when not hidden
				slide.show();

				const yOffset = slide._ferrisYOffset;
				const position = slide._renderPosition;

				// Build transform string - NO rotateZ, slides stay upright
				const transform = `translate3d(${position}px, ${yOffset}px, 0)`;

				// Only apply if transform actually changed
				if (_.lastTransform.get(slide) !== transform) {
					_.lastTransform.set(slide, transform);
					slide.style.transform = transform;
				}
			}
		}

		/**
		 * Clean up the effect when it's being destroyed or replaced
		 * Resets all slide transforms and styles to default state
		 */
		destroy() {
			super.destroy();

			// Clear track transform (in case it was set before)
			this.ctx.track.style.transform = '';

			// Clear transform cache
			this.lastTransform = null;

			// Reset all slides to default state
			const slides = this.ctx.store.getSlides();
			for (let i = 0; i < slides.length; i++) {
				const slide = slides[i];
				slide.style.transform = '';
				slide.show?.();
			}
		}
	};
}

// Queue the factory (works regardless of whether Tarot is loaded)
(window.TarotEffectQueue = window.TarotEffectQueue || []).push({
	effectName: 'ferris-wheel',
	factory: createFerrisWheelEffect,
});

export default createFerrisWheelEffect;
